import React, { useEffect, useState, useMemo } from 'react';
import 'moment/locale/it';
import Fuse from 'fuse.js';
import Accordion, { AccordionStyles } from '../Accordion/Accordion';
import SearchBar, {
  SearchBarVariants,
} from '../../ui-components/SearchBar/SearchBar';
import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import { TRANSLATIONS } from '../../../translations';
import FaqTopic from './FaqTopic';
import FaqElement from './FaqElement';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import Button, {
  ButtonSizes,
  ButtonStyles,
  ButtonWidths,
} from '../Button/Button';

const FaqSearch = ({
  faqs,
  hubspotFormType,
  phoneLink,
  startTime,
  endTime,
  isPhoneActive,
  partnerInfo = null,
  canShowPartnerInfo = null,
}) => {

  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [visibleResults, setVisibleResults] = useState(5);
  const RESULTS_PER_PAGE = 5;

  const flattenedFaq = useMemo(() => {
    try {
      const data = JSON.parse(faqs);
      const flattened = data.flatMap((item) =>
        item.faq_group.map((faq) => ({
          question: faq.question,
          answer: faq.answer,
          featured: faq.featured,
          priority: faq.priority,
          show_partner_contacts: faq.show_partner_contacts,
        }))
      );
      return flattened || [];
    } catch (e) {
      return [];
    }
  }, [faqs]);

  const parsedFaq = useMemo(() => {
    try {
      return JSON.parse(faqs) || [];
    } catch (e) {
      return [];
    }
  }, [faqs]);

  const featuredFaq = useMemo(() => {
    try {
      const filtered = flattenedFaq.filter((item) => item.featured);

      // Sort featured items by priority
      const sortedFeatured = filtered.sort((a, b) => {
        if (a.priority !== undefined && b.priority !== undefined) {
          return a.priority - b.priority;
        }
        if (a.priority !== undefined) {
          return -1;
        }
        if (b.priority !== undefined) {
          return 1;
        }
        return 0;
      });

      return sortedFeatured.length > 0 ? sortedFeatured : null;
    } catch (e) {
      return null;
    }
  }, [flattenedFaq]);

  // console.log('parsedFaq', parsedFaq);
  // console.log('flattenedFaq', flattenedFaq);
  // console.log('featuredFaq', featuredFaq);

  const fuse = useMemo(() => {
    return new Fuse(flattenedFaq, {
      keys: ['question', 'answer'],
      //includeScore: true,
      threshold: 0.6,
    });
  }, [flattenedFaq]);

  const handleLoadMore = () => {
    setVisibleResults((prevCount) => prevCount + RESULTS_PER_PAGE);
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults(null);
    } else {
      const searchResults = fuse
        .search(searchQuery)
        .map((result) => result.item);
      setResults(searchResults);
      setVisibleResults(RESULTS_PER_PAGE);
    }
  }, [searchQuery, fuse]);

  return (
    <div className="cell">
      <div className="padded-wrapper">
        <Typography
          classNames={'pb-m'}
          variant={TypographyVariants.p2}
          weight={TypographyWeights.bold}
        >
          {TRANSLATIONS.SEARCH_BY_TOPIC}
        </Typography>
      </div>

      <div className="input-wrapper d-flex flex-column">
        <SearchBar
          className="mb-2xl padded-wrapper search-bar--elevated"
          value={searchQuery}
          placeholder={TRANSLATIONS.BOOKING_ACTIVITY_PRICE}
          handleOnChange={(e) => setSearchQuery(e.target.value)}
          handleSearch={() => { }}
          keyboardHandler={() => { }}
          isAutoFocused={false}
          isAutoCompleted={false}
          variant={SearchBarVariants.primary}
        />
        {results?.length > 0 && (
          <div className="d-flex items-center gap-3xs mb-xs padded-wrapper">
            <BetaIcon
              name="search"
              size={IconSizes[16]}
              color={IconColors.regular}
            />
            <Typography
              variant={TypographyVariants.p2}
              weight={TypographyWeights.bold}
            >
              {TRANSLATIONS.RESULTS_OF_YOUR_SEARCH}
            </Typography>
          </div>
        )}
        {searchQuery && results?.length === 0 && (
          <div className="padded-wrapper mb-xs">
            <Typography
              variant={TypographyVariants.p2}
              weight={TypographyWeights.bold}
            >
              {TRANSLATIONS.WE_DIDNT_FIND_RESULTS_FOR_YOUR_SEARCH}
            </Typography>
          </div>
        )}
        {searchQuery &&
          results?.length === 0 &&
          featuredFaq?.slice(0, visibleResults).map((item, index) => {
            return (
              <FaqElement
                faq={item}
                hubspotFormType={hubspotFormType}
                phoneLink={phoneLink}
                index={index}
                variant="search"
                key={`search-result-${index}`}
                startTime={startTime}
                endTime={endTime}
                isPhoneActive={Number(isPhoneActive)}
                partnerInfo={partnerInfo}
                canShowPartnerInfo={canShowPartnerInfo}
              />
            );
          })}
        {results &&
          results.length > 0 &&
          results?.slice(0, visibleResults).map((item, index) => {
            return (
              <FaqElement
                faq={item}
                hubspotFormType={hubspotFormType}
                phoneLink={phoneLink}
                index={index}
                variant="search"
                key={`search-result-${index}`}
                startTime={startTime}
                endTime={endTime}
                isPhoneActive={Number(isPhoneActive)}
                partnerInfo={partnerInfo}
                canShowPartnerInfo={canShowPartnerInfo}
              />
            );
          })}
        {((results && results.length > visibleResults) ||
          (searchQuery &&
            results?.length === 0 &&
            featuredFaq &&
            featuredFaq.length > visibleResults)) && (
            <div className="d-flex justify-content-center mb-m mt-s w-100 faq-search-button">
              <Button
                size={ButtonSizes.s}
                style={ButtonStyles.empty}
                label={
                  <div className="d-flex items-center gap-3xs">
                    <Typography
                      variant={TypographyVariants.p3}
                      weight={TypographyWeights.bold}
                    >
                      {TRANSLATIONS.SEE_MORE}
                    </Typography>
                    <BetaIcon
                      name="chevronDown"
                      size={IconSizes[16]}
                      color={IconColors.regular}
                    />
                  </div>
                }
                onClick={handleLoadMore}
              />
            </div>
          )}
        {results && <div className="mb-xl w-100%"></div>}
      </div>

      <>
        <div className='padded-wrapper'>
          <Typography
            classNames={'pb-2xs'}
            variant={TypographyVariants.p2}
            weight={TypographyWeights.bold}
          >
            {TRANSLATIONS.OR_SELECT_A_TOPIC}
          </Typography>
        </div>

        {parsedFaq?.map((item, index) => {
          return (
            <FaqTopic
              faq={item}
              hubspotFormType={hubspotFormType}
              phoneLink={phoneLink}
              key={`faq-${index}`}
              partnerInfo={partnerInfo}
              startTime={startTime}
              endTime={endTime}
              isPhoneActive={Number(isPhoneActive)}
              canShowPartnerInfo={canShowPartnerInfo}
            />
          );
        })}
      </>
    </div>
  );
};

FaqSearch.propTypes = {};

export default FaqSearch;
